import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

// Track which limit types have already triggered an email this session
const sentLimitEmails = new Set<string>();

// Map usage types to human-readable names for the email template
const USAGE_TYPE_LABELS: Record<string, string> = {
  content_uploads: "Content Uploads",
  flashcards: "Flashcards",
  chat_messages: "AI Chat Messages",
  quiz_answers: "Quizzes",
  exams: "Exams",
};

async function sendUsageLimitEmail(
  usageType: string,
  limit: number
) {
  // Only send once per usage type per session
  if (sentLimitEmails.has(usageType)) return;
  sentLimitEmails.add(usageType);

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from("profiles")
      .select("name, email")
      .eq("id", user.id)
      .maybeSingle();

    const recipientEmail = profile?.email || user.email;
    if (!recipientEmail) return;

    await supabase.functions.invoke("send-email", {
      body: {
        event_type: "usage_limit",
        recipient_email: recipientEmail,
        recipient_name: profile?.name || "there",
        params: {
          LIMIT_TYPE: USAGE_TYPE_LABELS[usageType] || usageType,
          LIMIT_VALUE: String(limit),
          PLAN_NAME: "Free",
        },
      },
    });
    console.log(`Usage limit email sent for ${usageType}`);
  } catch (err) {
    console.error("Failed to send usage limit email:", err);
  }
}

export interface UsageLimits {
  contentUploads: { used: number; limit: number };
  flashcards: { used: number; limit: number };
  chatMessages: { used: number; limit: number };
  quizAnswers: { used: number; limit: number };
  practiceExams: { used: number; limit: number };
  dailyResetAt: string | null;
  monthlyResetAt: string | null;
  isFreeTier: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const FREE_TIER_LIMITS = {
  contentUploads: 3,
  flashcards: 10,
  chatMessages: 5,
  quizAnswers: 10,
  practiceExams: 2,
};

// Custom error class for usage limit exceeded
export class UsageLimitExceededError extends Error {
  usageType: "content_uploads" | "flashcards" | "chat_messages" | "quiz_answers" | "exams";
  used: number;
  limit: number;

  constructor(
    usageType: "content_uploads" | "flashcards" | "chat_messages" | "quiz_answers" | "exams",
    used: number,
    limit: number
  ) {
    super(`Usage limit exceeded: ${usageType}`);
    this.name = "UsageLimitExceededError";
    this.usageType = usageType;
    this.used = used;
    this.limit = limit;
  }
}

// Get user-friendly message for each usage type
export function getUsageLimitMessage(usageType: string): { title: string; description: string } {
  switch (usageType) {
    case "content_uploads":
      return {
        title: "Upload limit reached",
        description: "You've reached your daily upload limit. Upgrade for unlimited uploads.",
      };
    case "chat_messages":
      return {
        title: "Chat limit reached",
        description: "You've used all 5 daily AI chats. Upgrade to continue chatting.",
      };
    case "flashcards":
      return {
        title: "Flashcard limit reached",
        description: "You've used all 10 daily flashcard generations. Upgrade for unlimited.",
      };
    case "quiz_answers":
      return {
        title: "Quiz limit reached",
        description: "You've used all 10 daily quiz answers. Upgrade for unlimited quizzes.",
      };
    case "exams":
      return {
        title: "Exam limit reached",
        description: "You've used both monthly practice exams. Upgrade for unlimited exams.",
      };
    default:
      return {
        title: "Limit reached",
        description: "You've reached your usage limit. Upgrade for unlimited access.",
      };
  }
}

// Check if user can proceed with action - returns result without throwing
export async function checkUsageLimit(
  usageType: "content_uploads" | "flashcards" | "chat_messages" | "quiz_answers" | "exams"
): Promise<{ canProceed: boolean; used: number; limit: number }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      // No user = not logged in, can't proceed
      return { canProceed: false, used: 0, limit: 0 };
    }

    // Check subscription status
    const { data: profile } = await supabase
      .from("profiles")
      .select("subscription_status")
      .eq("id", user.id)
      .maybeSingle();

    const subscriptionStatus = profile?.subscription_status ?? "free";
    let isFreeTier = subscriptionStatus === "free" || !subscriptionStatus;

    // If free tier on profile, check if user has an active team subscription
    if (isFreeTier) {
      const { data: teamMembership } = await supabase
        .from("team_members")
        .select("team_id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (teamMembership?.team_id) {
        const { data: team } = await supabase
          .from("teams")
          .select("subscription_status")
          .eq("id", teamMembership.team_id)
          .single();

        if (team?.subscription_status === "active") {
          isFreeTier = false;
        }
      }
    }

    // Pro/Premium/Team users have unlimited access
    if (!isFreeTier) {
      return { canProceed: true, used: 0, limit: Infinity };
    }

    // Get current usage for free tier users
    const { data: usageData, error: usageError } = await supabase.rpc("get_or_create_usage", {
      user_id_param: user.id,
    });

    if (usageError || !usageData) {
      console.error("Error fetching usage:", usageError);
      // On error, allow action to proceed (fail open)
      return { canProceed: true, used: 0, limit: Infinity };
    }

    // Map usage type to field and limit
    let used = 0;
    let limit = 0;

    switch (usageType) {
      case "content_uploads":
        used = usageData.content_uploads_today ?? 0;
        limit = FREE_TIER_LIMITS.contentUploads;
        break;
      case "flashcards":
        used = usageData.flashcards_today ?? 0;
        limit = FREE_TIER_LIMITS.flashcards;
        break;
      case "chat_messages":
        used = usageData.chat_messages_today ?? 0;
        limit = FREE_TIER_LIMITS.chatMessages;
        break;
      case "quiz_answers":
        used = usageData.quiz_answers_today ?? 0;
        limit = FREE_TIER_LIMITS.quizAnswers;
        break;
      case "exams":
        used = usageData.exams_this_month ?? 0;
        limit = FREE_TIER_LIMITS.practiceExams;
        break;
    }

    const canProceed = used < limit;

    // Fire a one-time email when the user first hits a limit
    if (!canProceed) {
      sendUsageLimitEmail(usageType, limit);
    }

    return { canProceed, used, limit };
  } catch (err) {
    console.error("Error checking usage limit:", err);
    // On error, allow action to proceed (fail open)
    return { canProceed: true, used: 0, limit: Infinity };
  }
}

export function useUsageLimits(): UsageLimits {
  const [usage, setUsage] = useState({
    contentUploads: 0,
    flashcards: 0,
    chatMessages: 0,
    quizAnswers: 0,
    practiceExams: 0,
  });
  const [dailyResetAt, setDailyResetAt] = useState<string | null>(null);
  const [monthlyResetAt, setMonthlyResetAt] = useState<string | null>(null);
  const [isFreeTier, setIsFreeTier] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsage = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Fetch usage and subscription status in parallel
      const [usageResult, profileResult] = await Promise.all([
        supabase.rpc("get_or_create_usage", { user_id_param: user.id }),
        supabase.from("profiles").select("subscription_status").eq("id", user.id).single()
      ]);

      if (usageResult.error) {
        throw usageResult.error;
      }

      if (usageResult.data) {
        setUsage({
          contentUploads: usageResult.data.content_uploads_today ?? 0,
          flashcards: usageResult.data.flashcards_today ?? 0,
          chatMessages: usageResult.data.chat_messages_today ?? 0,
          quizAnswers: usageResult.data.quiz_answers_today ?? 0,
          practiceExams: usageResult.data.exams_this_month ?? 0,
        });
        setDailyResetAt(usageResult.data.daily_reset_at ?? null);
        setMonthlyResetAt(usageResult.data.monthly_reset_at ?? null);
      }

      // Check subscription status - only show limits for free tier
      const subscriptionStatus = profileResult.data?.subscription_status ?? 'free';
      let freeTier = subscriptionStatus === 'free' || !subscriptionStatus;

      // Also check team subscription
      if (freeTier) {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          const { data: teamMembership } = await supabase
            .from("team_members")
            .select("team_id")
            .eq("user_id", authUser.id)
            .maybeSingle();

          if (teamMembership?.team_id) {
            const { data: team } = await supabase
              .from("teams")
              .select("subscription_status")
              .eq("id", teamMembership.team_id)
              .single();

            if (team?.subscription_status === "active") {
              freeTier = false;
            }
          }
        }
      }

      setIsFreeTier(freeTier);
    } catch (err) {
      console.error("Error fetching usage limits:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch usage"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return {
    contentUploads: { used: usage.contentUploads, limit: FREE_TIER_LIMITS.contentUploads },
    flashcards: { used: usage.flashcards, limit: FREE_TIER_LIMITS.flashcards },
    chatMessages: { used: usage.chatMessages, limit: FREE_TIER_LIMITS.chatMessages },
    quizAnswers: { used: usage.quizAnswers, limit: FREE_TIER_LIMITS.quizAnswers },
    practiceExams: { used: usage.practiceExams, limit: FREE_TIER_LIMITS.practiceExams },
    dailyResetAt,
    monthlyResetAt,
    isFreeTier,
    isLoading,
    error,
    refetch: fetchUsage,
  };
}

// Helper function to increment usage - can be called from anywhere
export async function incrementUsage(usageType: "content_uploads" | "flashcards" | "chat_messages" | "quiz_answers" | "exams"): Promise<{ exceeded: boolean; newCount: number } | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase.rpc("increment_usage", {
      user_id_param: user.id,
      usage_type: usageType,
    });

    if (error) {
      console.error("Error incrementing usage:", error);
      return null;
    }

    // Parse the JSON response
    const result = data as { exceeded?: boolean; new_count?: number } | null;

    return {
      exceeded: result?.exceeded ?? false,
      newCount: result?.new_count ?? 0,
    };
  } catch (err) {
    console.error("Error incrementing usage:", err);
    return null;
  }
}
